import { useState, type FormEvent } from 'react';
import { Brand } from './Brand';

interface OrderLookupProps {
  onLookup: (input: { email?: string; orderNumber?: string }) => void;
  loading: boolean;
  error?: string | null;
  initialEmail?: string;
  initialOrderNumber?: string;
}

export function OrderLookup({
  onLookup,
  loading,
  error,
  initialEmail = '',
  initialOrderNumber = '',
}: OrderLookupProps) {
  const [email, setEmail] = useState(initialEmail);
  const [orderNumber, setOrderNumber] = useState(initialOrderNumber);

  const trimmedOrderNumber = orderNumber.trim();
  const orderNumberDigits = trimmedOrderNumber.replace(/\D/g, '').length;
  const canSubmit = !!email.trim() && orderNumberDigits >= 5 && !loading;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onLookup({
      email: email.trim(),
      orderNumber: trimmedOrderNumber,
    });
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] px-4 py-10">
      <div className="mb-8 text-center">
        <div className="mb-5 flex justify-center">
          <Brand />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Track Your Order</h1>
        <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
          Enter the email address on your order along with your order number.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="w-full max-w-md space-y-3">
        <div>
          <label htmlFor="lookup-email" className="block text-xs font-medium text-slate-500 mb-1">
            Email address <span className="text-red-500">*</span>
          </label>
          <input
            id="lookup-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            placeholder="you@example.com"
            className="
              w-full h-12 px-4 rounded-xl bg-white border-2 border-slate-200
              text-base text-slate-900 placeholder:text-slate-300
              focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-100
              transition-all duration-200 disabled:opacity-60
            "
          />
        </div>

        <div>
          <label htmlFor="lookup-order" className="block text-xs font-medium text-slate-500 mb-1">
            Order Number <span className="text-red-500">*</span>
          </label>
          <input
            id="lookup-order"
            type="text"
            inputMode="text"
            autoComplete="off"
            required
            minLength={5}
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            disabled={loading}
            placeholder="12345"
            className="
              w-full h-12 px-4 rounded-xl bg-white border-2 border-slate-200
              text-base font-mono text-slate-900 placeholder:text-slate-300
              focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-100
              transition-all duration-200 disabled:opacity-60
            "
          />
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <button type="submit" disabled={!canSubmit} className="btn-pill-primary w-full h-12 text-base">
          {loading ? (
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            'Look Up Order'
          )}
        </button>
      </form>

      <p className="mt-6 text-[11px] text-slate-400 text-center max-w-md">
        Have a tracking link? It will load your order automatically. If you reached this page
        directly, use the form above.
      </p>
    </div>
  );
}
